import type {
	CommandConstructor,
	CommandProperties,
} from '@atlassian/rovo-content-bridge-api/command';
import { createCommand } from '@atlassian/rovo-content-bridge-api/command-creator';

/**
 * The placement describes where/how the content lands in the editor.
 * - `'at-cursor'` — insert at the current cursor position (default editor behaviour)
 * - `'before-block'` — insert before the current block node
 * - `'after-block'` — insert after the current block node
 * - `'replace-selection'` — replace the current selection with the content
 */
export type EditorInsertContentPlacement =
	| 'at-cursor'
	| 'before-block'
	| 'after-block'
	| 'replace-selection';

/**
 * `data` mirrors the document format convention used in other bridge API commands.
 * `placement` is present on all content variants and describes where content lands.
 */
export type EditorInsertContentCommandAction =
	| {
			mode: 'insert';
			placement: EditorInsertContentPlacement;
			useStoredSelection?: boolean;
			data: {
				type: 'text/markdown';
				content: string;
			};
	  }
	| {
			mode: 'insert';
			placement: EditorInsertContentPlacement;
			restoreSelection?: boolean;
			// `object` to avoid adding '@atlaskit/adf-schema' as dependency
			data: {
				type: 'text/adf';
				content: object;
			};
	  }
	| {
			mode: 'upload-and-insert-media';
			placement: EditorInsertContentPlacement;
			useStoredSelection?: boolean;
			data: {
				// Can be narrowed down to mime types later
				type: string;
				url: string;
			};
	  };

/**
 * Properties for the generic editor insert content command.
 */
export type EditorInsertContentCommandProperties = CommandProperties &
	EditorInsertContentCommandAction;

/**
 * A generic command sent by a bridge API client to insert or replace content in an editor.
 *
 * @summary {@link CommandProperties.senderId} is requesting {@link CommandProperties.receiverId}
 * to insert or replace content in the editor at the current cursor position or selection.
 *
 * @description This is the bridge API replacement for the legacy `usePublish` + `editor-suggestion`
 * pub/sub pattern from `@atlaskit/rovo-triggers`. Prefer this command for all new integrations.
 */
export const EditorInsertContentCommand: CommandConstructor<
	EditorInsertContentCommandProperties,
	void
> = createCommand<EditorInsertContentCommandProperties, void>('editor-insert-content-command');
